import { Box, Card, Chip, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { useQuery } from "@tanstack/react-query";
import { Layers } from "lucide-react";
import { useMemo } from "react";
import type { JSX, ReactNode } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";

import { NoAccessPanel } from "../../components/ui/no-access-panel.js";
import { PageHeader } from "../../components/ui/page-header.js";
import { PageLoader } from "../../components/ui/page-loader.js";
import { isPermissionDenied } from "../../lib/api.js";
import { listAllDocs, listCategories } from "./api.js";
import { ArticleList, ArticleRow } from "./ArticleRow.js";
import { DocSearchBox } from "./DocSearchBox.js";
import { searchDocs } from "./docSearch.js";
import { FolderCard, FolderGrid, folderCardSx, folderIconSx } from "./FolderCard.js";
import { isLibraryDoc, sortNewestFirst } from "./folder-filters.js";
import { folderCount, subFolders, topLevelFolders } from "./folder-tree.js";
import { KB_RELEASES_PATH } from "./kb-paths.js";
import type { DocSummary } from "./types.js";

/**
 * The Documentation landing page (2026-09-14): the customer portal's shape,
 * because a partner reads exactly the way a customer does — a search box,
 * then the FOLDERS as cards, then Featured and Recently added.
 *
 * DOCUMENTATION IS THE LIBRARY SHELF. A document pinned to a release is
 * release documentation and is read under that release on the Releases page;
 * nothing here lists it twice. Search is the one exception — it spans both
 * shelves, because the shelf is a browse axis, not a search wall — and a
 * release-pinned hit carries its version chip so the reader knows where it
 * lives.
 *
 * RELEASE DOCUMENTS (2026-09-17). The one way in to release documentation
 * from here is the pinned **Release documents** folder card, first among the
 * folders: it opens `/documentation/releases` (every release) and then one
 * release's versions with their documents. It is a door, not a second copy —
 * Featured and Recently added stay library-only, and the card holds no count
 * so the landing page makes no releases request of its own.
 *
 * No sidebar tree. The rail is the portal's navigation and the folder cards
 * are the documentation's; a three-column tree that landed a partner on a
 * VERSION page was what this replaced.
 *
 * FOLDERS NEST (2026-10-31). The cards are the TOP-LEVEL folders of the
 * tree portal-api builds from what this company may read; a sub-folder opens
 * from its parent's page, in the command center's place and order
 * (`folder-tree.ts`). A folder staff did not allow for the company is not in
 * the list at all.
 *
 * Everything is read from ONE complete pull of the curated list
 * (`listAllDocs`, paginated to the end) so the folder counts, the two rails
 * and the search results can never disagree with each other. The curated
 * library is small; the tree used to read the same list.
 */
export function KnowledgeBasePage(): JSX.Element {
  const [params] = useSearchParams();
  const q = (params.get("q") ?? "").trim();

  const cats = useQuery({ queryKey: ["docs", "categories"], queryFn: listCategories });
  const all = useQuery({ queryKey: ["docs", "all"], queryFn: () => listAllDocs() });

  const library = useMemo(() => (all.data ?? []).filter(isLibraryDoc), [all.data]);
  const results = useMemo(() => searchDocs(all.data ?? [], q), [all.data, q]);
  const featured = useMemo(() => sortNewestFirst(library.filter((d) => d.featured)).slice(0, 6), [library]);
  const recent = useMemo(() => sortNewestFirst(library).slice(0, 6), [library]);
  // The TOP-LEVEL folders only: a sub-folder is reached through its parent,
  // exactly where it sits in the command center's tree (`folder-tree.ts`).
  const folders = useMemo(() => {
    const all = cats.data?.data ?? [];
    return topLevelFolders(all, library).map((c) => ({
      category: c,
      count: folderCount(c, all, library),
      subFolderCount: subFolders(c, all).length,
    }));
  }, [cats.data, library]);

  if (cats.isLoading || all.isLoading) return <PageLoader />;

  const error = cats.error ?? all.error;
  if (error) {
    if (isPermissionDenied(error)) {
      return <NoAccessPanel permission={error.permission ?? "documentation:view"} />;
    }
    return (
      <Box sx={{ display: "grid", gap: 2.5 }}>
        <PageHeader title="Documentation" />
        <Notice testId="kb-error">
          We could not reach the documentation:{" "}
          {error instanceof Error ? error.message : "unknown error"}. Try again in a moment.
        </Notice>
      </Box>
    );
  }

  return (
    <Box sx={{ display: "grid", gap: 3 }} data-testid="documentation-page">
      <PageHeader
        title="Documentation"
        description="Guides, articles, FAQs and videos XOP Networks shares with your company."
      />

      <DocSearchBox />

      {q ? (
        <SearchResults q={q} results={results} />
      ) : library.length === 0 ? (
        <Notice testId="kb-empty">
          <Typography sx={{ fontSize: 14, fontWeight: 500 }}>
            No articles have been shared with you yet.
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: 12, color: "text.disabled" }}>
            Your XOP Networks contact decides which folders appear here.
          </Typography>
        </Notice>
      ) : null}

      {/* Release documents are not the library, so an empty library still
          offers the door to them. */}
      {!q && library.length === 0 && (
        <FolderGrid>
          <ReleaseDocumentsCard />
        </FolderGrid>
      )}

      {q || library.length === 0 ? null : (
        <>
          <Section title="Folders" subtitle="Browse by topic." testId="kb-folders">
            <FolderGrid>
              <ReleaseDocumentsCard />
              {folders.map(({ category, count, subFolderCount }) => (
                <FolderCard key={category.id} category={category} count={count} subFolderCount={subFolderCount} />
              ))}
            </FolderGrid>
          </Section>

          {featured.length > 0 && (
            <Section title="Featured" subtitle="Picked out by XOP Networks." testId="kb-featured">
              <ArticleList testId="kb-featured-list">
                {featured.map((d) => (
                  <ArticleRow key={d.id} doc={d} showFolder />
                ))}
              </ArticleList>
            </Section>
          )}

          <Section title="Recently added" subtitle="The newest articles across every folder." testId="kb-recent">
            <ArticleList testId="kb-recent-list">
              {recent.map((d) => (
                <ArticleRow key={d.id} doc={d} showFolder />
              ))}
            </ArticleList>
          </Section>
        </>
      )}
    </Box>
  );
}

/** Results replace the landing sections while a query is typed. */
function SearchResults({ q, results }: { q: string; results: DocSummary[] }): JSX.Element {
  if (results.length === 0) {
    return (
      <Notice testId="kb-search-empty">
        No articles match <strong>“{q}”</strong>. Try fewer or different words.
      </Notice>
    );
  }
  return (
    <Box sx={{ display: "grid", gap: 1.5 }}>
      <Typography data-testid="kb-search-count" sx={{ fontSize: 13, color: "text.secondary" }}>
        {results.length} result{results.length === 1 ? "" : "s"} for <strong>“{q}”</strong>
      </Typography>
      <ArticleList testId="kb-search-results">
        {results.map((d) => (
          <ArticleRow key={d.id} doc={d} showFolder showVersion />
        ))}
      </ArticleList>
    </Box>
  );
}

/**
 * The pinned **Release documents** folder — always first. Styled like every
 * folder card (the same `folderCardSx`) with a "Pinned" chip in place of an
 * article count.
 */
function ReleaseDocumentsCard(): JSX.Element {
  return (
    <Card component={RouterLink} to={KB_RELEASES_PATH} data-testid="kb-folder-releases" sx={folderCardSx}>
      <Box aria-hidden sx={folderIconSx}>
        <Layers size={18} />
      </Box>
      <Typography component="h4" sx={{ fontSize: 15, fontWeight: 600, lineHeight: 1.3 }}>
        Release documents
      </Typography>
      <Typography sx={{ fontSize: 12.5, color: "text.secondary" }}>
        Every document that ships with a release, by version.
      </Typography>
      <Chip label="Pinned" size="small" variant="outlined" sx={{ justifySelf: "start", height: 18, fontSize: 11 }} />
    </Card>
  );
}

function Section({
  title,
  subtitle,
  testId,
  children,
}: {
  title: string;
  subtitle: string;
  testId: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <Box component="section" data-testid={testId} aria-label={title} sx={{ display: "grid", gap: 1.5 }}>
      <Box>
        <Typography component="h3" sx={{ fontSize: 16, fontWeight: 600 }}>
          {title}
        </Typography>
        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>{subtitle}</Typography>
      </Box>
      {children}
    </Box>
  );
}

function Notice({ testId, children }: { testId: string; children: ReactNode }): JSX.Element {
  return (
    <Box
      data-testid={testId}
      sx={{
        borderRadius: 1,
        border: 1,
        borderColor: "divider",
        bgcolor: (t) => alpha(t.palette.surfaceSoft, 0.4),
        p: 4,
        textAlign: "center",
        fontSize: 14,
        color: "text.secondary",
      }}
    >
      {children}
    </Box>
  );
}
